import { Container } from '@afterpaytouch/core'
import { useRouter } from 'next/router'
import React, { FunctionComponent, useState, ReactNode, useEffect, ReactElement, useRef } from 'react'

import { useCheckoutState, useCountry, useDisplayProcessingModal, useIsAfterpayAppGiftCardFlow, useShowLoaderOnly, useAPFooter } from '../../state'
import { AppContainer, Footer, Header, PageTransition, OrderComplete, ProcessingModal, PageTransitionDirection } from '../../component'
import { CheckoutStatus, Features } from '../../model'
import { useFlag } from '@afterpaytouch/feature'
import { HeaderType } from '../types'
import { useRouteBackLink } from '../../config/router'
import styles from './style.module.css'
import clsx from 'clsx'
import { MaintenanceModeContent } from '../../component/MaintenanceModeContent/MaintenanceModeContent'
import { isNullOrUndefined } from '@afterpay/utils'
import { WhiteLabelLayout } from '../WhiteLabelLayout'
import { useAllowClose, useHideFooterForUSLoginPage } from './hooks'

interface Props {
  headerType?: HeaderType
  allowClose?: boolean
  hasMargin?: boolean
  children?: ReactNode
  allowFooter?: boolean
  allowPostCheckout?: boolean
  hasDefaultProcessingTimeout?: boolean
}

export const MainLayout: FunctionComponent<Props> = ({
  children,
  headerType = HeaderType.Standard,
  allowClose = true,
  hasMargin = true,
  allowFooter = true,
  allowPostCheckout = true,
  hasDefaultProcessingTimeout = false,
}) => {
  const country = useCountry()
  const router = useRouter()
  const { asPath } = router
  const { status } = useCheckoutState()
  const [initialStatus] = useState(status)
  const isClosed = initialStatus === CheckoutStatus.Closed
  const backLink = useRouteBackLink()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const showLoaderOnly = useShowLoaderOnly()
  const shouldUseAPFooter = useAPFooter()
  const hideFooterForUSLoginPage = useHideFooterForUSLoginPage()

  const [pageTransitionDirection, setPageTransitionDirection] = useState(PageTransitionDirection.Forward)
  const routeHistoryRef = useRef<string[]>([])
  const routeHistoryIndexRef = useRef<number>(-1)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const normalizePath = (url: string): string => url.split('?')[0].split('#')[0]

    const onRouteChangeStart = (url: string) => {
      const next = normalizePath(url)

      // Initialize with current route on first transition.
      if (routeHistoryIndexRef.current === -1) {
        routeHistoryRef.current = [normalizePath(router.asPath)]
        routeHistoryIndexRef.current = 0
      }

      const stack = routeHistoryRef.current
      const idx = routeHistoryIndexRef.current

      const prev = idx > 0 ? stack[idx - 1] : null
      const fwd = idx + 1 < stack.length ? stack[idx + 1] : null

      if (prev != null && next === prev) {
        routeHistoryIndexRef.current = idx - 1
        setPageTransitionDirection(PageTransitionDirection.Backward)
        return
      }

      if (fwd != null && next === fwd) {
        routeHistoryIndexRef.current = idx + 1
        setPageTransitionDirection(PageTransitionDirection.Forward)
        return
      }

      // New navigation: drop any forward entries and push the new route.
      routeHistoryRef.current = [...stack.slice(0, idx + 1), next]
      routeHistoryIndexRef.current = idx + 1
      setPageTransitionDirection(PageTransitionDirection.Forward)
    }

    const onRouteChangeComplete = () => {}
    const onRouteChangeError = () => {}

    router.events?.on?.('routeChangeStart', onRouteChangeStart)
    router.events?.on?.('routeChangeComplete', onRouteChangeComplete)
    router.events?.on?.('routeChangeError', onRouteChangeError)

    return () => {
      router.events?.off?.('routeChangeStart', onRouteChangeStart)
      router.events?.off?.('routeChangeComplete', onRouteChangeComplete)
      router.events?.off?.('routeChangeError', onRouteChangeError)
    }
  }, [router])

  const finalAllowClose = useAllowClose(allowClose)

  const isMaintenanceMode = useFlag(Features.MAINTENANCE_MODE)

  const isConfirmChangeToClose = !isClosed && status === CheckoutStatus.Closed
  const isConfirmed = status === CheckoutStatus.Confirmed || isConfirmChangeToClose
  const displayProcessingModal = useDisplayProcessingModal()

  const containerStyles = clsx(!showLoaderOnly && styles.container)
  const showFooter = allowFooter && !showLoaderOnly && shouldUseAPFooter
  const shouldRenderFooter = !showLoaderOnly && !hideFooterForUSLoginPage
  const [isGiftCardFlow, setIsGiftCardFlow] = useState<boolean | undefined>(undefined)

  // persist gift card flow on processing modal
  useEffect(() => {
    if (isNullOrUndefined(isGiftCardFlow) || isAfterpayAppGiftCardFlow) {
      setIsGiftCardFlow(isAfterpayAppGiftCardFlow)
    }
  }, [isAfterpayAppGiftCardFlow])

  return (
    <Container>
      <AppContainer hasMargin={hasMargin} hasMobileTopMargin={!showLoaderOnly} lockScroll={isConfirmed}>
        {displayProcessingModal && !isClosed && (
          <ProcessingModal allowPostCheckout={allowPostCheckout} hasDefaultTimeout={hasDefaultProcessingTimeout} isAPGiftCardFlow={isGiftCardFlow} />
        )}
        <div className={containerStyles}>
          {!showLoaderOnly && (
            <Header
              country={country}
              allowClose={finalAllowClose}
              type={headerType}
              {...(headerType === HeaderType.Collapsed && {
                backLink,
              })}
            />
          )}
          {!isClosed && !isMaintenanceMode && (
            <PageTransition location={asPath} removeTransition={showLoaderOnly} direction={pageTransitionDirection}>
              {children}
            </PageTransition>
          )}
          {isClosed && !isMaintenanceMode && <OrderComplete />}
          {isMaintenanceMode && <MaintenanceModeContent />}
        </div>
        <Footer visible={showFooter} shouldRender={shouldRenderFooter} />
      </AppContainer>
    </Container>
  )
}

export const getLayout = (props?: Props) => (page: ReactElement) => {
  const flowType = page?.props?.flowName

  if (flowType === 'apple-paylater') {
    return (
      <WhiteLabelLayout {...props} allowFooter={true}>
        {page}
      </WhiteLabelLayout>
    )
  }

  return <MainLayout {...props}>{page}</MainLayout>
}
